/**
 * Sets up a window message event listener
 * @param {string} eventType - The type of event to listen for
 * @param {Function} callback - Function to call when event is received
 * @returns {Function} - Cleanup function to remove event listener
 */
export const setupPostMessageListener = (eventType, callback) => {
  if (!eventType || typeof callback !== 'function') {
    console.error('Invalid parameters for setupPostMessageListener');
    return () => {}; // Return empty cleanup function
  }

  const messageHandler = (event) => {
    // Validate the origin if needed
    // if (event.origin !== 'https://app.statzon.com') return;
    
    try {
      // For messages that are already objects
      if (typeof event.data === 'object' && event.data !== null) {
        // Handle format: { type: 'Statzon-chart-type', chartType: 'bar' }
        if (event.data.type === eventType && event.data.chartType) {
          callback({ chartType: event.data.chartType });
        }
        // Handle format: { event: 'statzon_chart_type', type: 'ver_bar' }
        else if (event.data.event === 'statzon_chart_type' && event.data.type) {
          callback({ chartType: event.data.type });
        }
      } 
      // For stringified JSON messages
      else if (typeof event.data === 'string') {
        try {
          const data = JSON.parse(event.data);
          if (data) {
            // Handle format: { type: 'Statzon-chart-type', chartType: 'bar' }
            if (data.type === eventType && data.chartType) {
              callback({ chartType: data.chartType });
            }
            // Handle format: { event: 'statzon_chart_type', type: 'ver_bar' }
            else if (data.event === 'statzon_chart_type' && data.type) {
              callback({ chartType: data.type });
            }
          }
        } catch (e) {
          // Not JSON or invalid format
        }
      }
    } catch (error) {
      console.error('Error processing postMessage event:', error);
    }
  };

  window.addEventListener('message', messageHandler);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('message', messageHandler);
  };
};

/**
 * Dispatches a card action event via postMessage to the parent window.
 * Other frontend projects can listen with: window.addEventListener('message', handler)
 * Event shape: { type: 'FP_CARD_ACTION', action: string, data: object }
 *
 * @param {'vote'|'rating'|'comment_submit'|'comment_edit'} action
 * @param {Object} data - Action-specific payload
 */
export const dispatchCardAction = (action, data) => {
  try {
    window.parent.postMessage({ type: 'FP_CARD_ACTION', action, data }, '*');
  } catch (e) {
    // silently ignore cross-origin errors
  }
};

/**
 * Parses StatZon chart URL to extract base URL and type
 * @param {string} url - Full StatZon chart URL
 * @returns {Object} Object containing baseUrl and type
 */
export const parseStatzonChartUrl = (url) => {
  if (!url || typeof url !== 'string') return { baseUrl: '', type: '' };
  
  const typeIndex = url.indexOf('?type=');
  
  if (typeIndex === -1) {
    return { baseUrl: url, type: '' };
  }
  
  const baseUrl = url.substring(0, typeIndex);
  const type = url.substring(typeIndex + 6); // +6 to skip '?type='
  
  return { baseUrl, type };
};

/**
 * Builds a StatZon chart URL with the given base URL and type
 * @param {string} baseUrl - Base URL without type parameter
 * @param {string} type - Chart type to append
 * @returns {string} Complete StatZon chart URL
 */
export const buildStatzonChartUrl = (baseUrl, type) => {
  if (!baseUrl || typeof baseUrl !== 'string') return '';
  if (!type || typeof type !== 'string') return baseUrl;
  
  return `${baseUrl}?type=${type}`;
}; 